import { HttpError } from '@tomra/client-side-http-client';
import type { components } from '../../api-types';

/**
 * Structured scope-conflict handling for HTTP 409 responses from
 * `POST /manifests` and `PUT /manifests/{manifestId}/scope-tree`.
 *
 * The backend returns an RFC7807 problem+json body with a top-level `conflict`
 * extension member. The frontend branches on `conflict.type` (the stable
 * discriminator) — NOT the outer RFC7807 `type`.
 *
 * NOTE ON TYPES: `openapi-typescript` rewrites the discriminator property of a
 * `oneOf` to the *schema name* literal (e.g. "ScopeOverlapConflict") rather than
 * the real wire enum value (e.g. "SCOPE_OVERLAP"). We therefore do not rely on
 * the generated `conflict.type` literals and instead model the union here against
 * the real {@link ScopeConflictType} wire values, reusing the generated
 * sub-schemas for every other field.
 */

/** Stable discriminator the frontend branches on. */
export type ScopeConflictType = components['schemas']['ScopeConflictType'];

export const SCOPE_OVERLAP: ScopeConflictType = 'SCOPE_OVERLAP';
export const MULTIPAC_SPLIT: ScopeConflictType = 'MULTIPAC_SPLIT';

/** A single manifest whose machines overlap the requested scope. */
export type ConflictingManifest = components['schemas']['ConflictingManifest'];

/** A single linked multipac pair split across the scope boundary. */
export type ConflictingPair = components['schemas']['ConflictingPair'];

/**
 * Human-readable label for the excluded machine's owning manifest, mirroring
 * `manifestName ?? manifestId`. Returns `null` when the machine is unassigned
 * (no `excludedManifestId`), signalling the caller to render a placeholder.
 */
export function pairManifestLabel(pair: ConflictingPair): string | null {
  if (!pair.excludedManifestId) {
    return null;
  }
  return pair.excludedManifestName ?? pair.excludedManifestId;
}

/** Machines in the requested scope already belong to other manifests. */
export type ScopeOverlapConflict = Omit<components['schemas']['ScopeOverlapConflict'], 'type'> & {
  type: 'SCOPE_OVERLAP';
};

/** Linked multipac machine pairs would be split across the scope boundary. */
export type MultipacSplitConflict = Omit<components['schemas']['MultipacSplitConflict'], 'type'> & {
  type: 'MULTIPAC_SPLIT';
};

/**
 * The closed-today-but-designed-to-grow conflict union. Consumers must handle an
 * unknown `type` defensively so a future third variant degrades gracefully.
 */
export type ScopeConflict =
  | ScopeOverlapConflict
  | MultipacSplitConflict
  | { type: string; [key: string]: unknown };

/** The RFC7807 envelope carrying the flattened `conflict` extension member. */
export type ScopeConflictError = Omit<components['schemas']['ScopeConflictError'], 'conflict'> & {
  conflict: ScopeConflict;
};

/** Narrows an unknown conflict to the SCOPE_OVERLAP shape. */
export function isScopeOverlap(conflict: ScopeConflict): conflict is ScopeOverlapConflict {
  return conflict.type === SCOPE_OVERLAP;
}

/** Narrows an unknown conflict to the MULTIPAC_SPLIT shape. */
export function isMultipacSplit(conflict: ScopeConflict): conflict is MultipacSplitConflict {
  return conflict.type === MULTIPAC_SPLIT;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Attempts to read a structured scope conflict from a thrown error.
 *
 * Returns the parsed {@link ScopeConflictError} envelope only when the error is
 * an HTTP 409 whose body carries a `conflict` object with a string `conflict.type`.
 * Returns `null` for anything else (other statuses, malformed bodies, non-conflict
 * 409s) so existing generic error handling continues to run unchanged.
 *
 * The HTTP client already JSON-parses response bodies regardless of content type,
 * so `problem+json` bodies are never discarded. We still defensively re-parse a
 * string body in case a raw string slips through.
 */
export function parseScopeConflict(e: unknown): ScopeConflictError | null {
  if (!(e instanceof HttpError) || e.status !== 409) {
    return null;
  }

  let body: unknown = e.body;
  if (typeof body === 'string') {
    const trimmed = body.trim();
    if (!(trimmed.startsWith('{') && trimmed.endsWith('}'))) {
      return null;
    }
    try {
      body = JSON.parse(trimmed);
    } catch {
      return null;
    }
  }

  if (!isRecord(body)) {
    return null;
  }

  const conflict = body.conflict;
  if (!isRecord(conflict) || typeof conflict.type !== 'string') {
    return null;
  }

  if (typeof body.title !== 'string' || typeof body.errorId !== 'string') {
    return null;
  }

  // Validate the discriminated arrays before casting so downstream components can
  // safely spread/destructure them. Unknown (future) types carry no array
  // requirement and pass through for graceful degradation.
  if (conflict.type === SCOPE_OVERLAP && !Array.isArray(conflict.manifests)) {
    return null;
  }
  if (conflict.type === MULTIPAC_SPLIT && !Array.isArray(conflict.samplePairs)) {
    return null;
  }

  return body as unknown as ScopeConflictError;
}
